import React, { useContext, useCallback, useState, useEffect } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import {
  Box,
  IconButton,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import TitleIcon from '@mui/icons-material/Title';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette, typography, theme as themeColors } from '../theme/colors';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
}

interface MenuBarProps {
  editor: Editor | null;
  colors: ReturnType<typeof getColors>;
  disabled?: boolean;
}

const getColors = (mode: 'light' | 'dark') => mode === 'dark' ? {
  bg: palette.surface,
  bgElevated: palette.surfaceElevated,
  bgHover: palette.surfaceHover,
  border: palette.border,
  text: palette.textPrimary,
  textSecondary: palette.textSecondary,
  textMuted: palette.textMuted,
} : {
  bg: palette.light.surface,
  bgElevated: palette.light.surfaceElevated,
  bgHover: palette.light.surfaceHover,
  border: palette.light.border,
  text: palette.light.textPrimary,
  textSecondary: palette.light.textSecondary,
  textMuted: palette.light.textMuted,
};

const MenuButton = ({
  onClick,
  isActive,
  disabled,
  tooltip,
  children,
  colors,
}: {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  isActive?: boolean;
  disabled?: boolean;
  tooltip: string;
  children: React.ReactNode;
  colors: ReturnType<typeof getColors>;
}) => (
  <Tooltip title={tooltip} arrow>
    <span>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        size="small"
        sx={{
          borderRadius: themeColors.radius.sm,
          backgroundColor: isActive ? palette.primary + '20' : 'transparent',
          color: isActive ? palette.primary : colors.textSecondary,
          '&:hover': {
            backgroundColor: isActive ? palette.primary + '30' : colors.bgHover,
          },
          '&.Mui-disabled': {
            color: colors.textMuted,
          },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const MenuBar = ({ editor, colors, disabled }: MenuBarProps) => {
  const [tableMenuAnchor, setTableMenuAnchor] = useState<null | HTMLElement>(null);

  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleTableMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTableMenuAnchor(event.currentTarget);
  };

  const handleTableMenuClose = () => {
    setTableMenuAnchor(null);
  };

  // Table commands - cast to any to bypass TipTap's incomplete type definitions
  const insertTable = () => {
    (editor?.chain().focus() as any).insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run();
    handleTableMenuClose();
  };

  const addColumnBefore = () => {
    (editor?.chain().focus() as any).addColumnBefore().run();
    handleTableMenuClose();
  };

  const addColumnAfter = () => {
    (editor?.chain().focus() as any).addColumnAfter().run();
    handleTableMenuClose();
  };

  const deleteColumn = () => {
    (editor?.chain().focus() as any).deleteColumn().run();
    handleTableMenuClose();
  };

  const addRowBefore = () => {
    (editor?.chain().focus() as any).addRowBefore().run();
    handleTableMenuClose();
  };

  const addRowAfter = () => {
    (editor?.chain().focus() as any).addRowAfter().run();
    handleTableMenuClose();
  };

  const deleteRow = () => {
    (editor?.chain().focus() as any).deleteRow().run();
    handleTableMenuClose();
  };

  const deleteTable = () => {
    (editor?.chain().focus() as any).deleteTable().run();
    handleTableMenuClose();
  };

  if (!editor) return null;

  const isInTable = editor.isActive('table');

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        p: 1,
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.bgElevated,
        borderTopLeftRadius: themeColors.radius.md,
        borderTopRightRadius: themeColors.radius.md,
      }}
    >
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        disabled={disabled}
        tooltip="Bold"
        colors={colors}
      >
        <FormatBoldIcon fontSize="small" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        disabled={disabled}
        tooltip="Italic"
        colors={colors}
      >
        <FormatItalicIcon fontSize="small" />
      </MenuButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: colors.border }} />

      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        disabled={disabled}
        tooltip="Heading"
        colors={colors}
      >
        <TitleIcon fontSize="small" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        disabled={disabled}
        tooltip="Bullet List"
        colors={colors}
      >
        <FormatListBulletedIcon fontSize="small" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        disabled={disabled}
        tooltip="Numbered List"
        colors={colors}
      >
        <FormatListNumberedIcon fontSize="small" />
      </MenuButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: colors.border }} />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        disabled={disabled}
        tooltip="Quote"
        colors={colors}
      >
        <FormatQuoteIcon fontSize="small" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        disabled={disabled}
        tooltip="Code Block"
        colors={colors}
      >
        <CodeIcon fontSize="small" />
      </MenuButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: colors.border }} />

      <MenuButton
        onClick={setLink}
        isActive={editor.isActive('link')}
        disabled={disabled}
        tooltip="Add Link"
        colors={colors}
      >
        <LinkIcon fontSize="small" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={disabled || !editor.isActive('link')}
        tooltip="Remove Link"
        colors={colors}
      >
        <LinkOffIcon fontSize="small" />
      </MenuButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: colors.border }} />

      <MenuButton
        onClick={handleTableMenuOpen}
        isActive={isInTable}
        disabled={disabled}
        tooltip="Table"
        colors={colors}
      >
        <TableChartIcon fontSize="small" />
      </MenuButton>

      <Menu
        anchorEl={tableMenuAnchor}
        open={Boolean(tableMenuAnchor)}
        onClose={handleTableMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            backgroundColor: colors.bgElevated,
            border: `1px solid ${colors.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        {!isInTable ? (
          <MenuItem onClick={insertTable} sx={{ color: colors.text, fontSize: '0.875rem' }}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} /> Insert Table (2×3)
          </MenuItem>
        ) : (
          <>
            <MenuItem onClick={addColumnBefore} sx={{ color: colors.text, fontSize: '0.875rem' }}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add Column Before
            </MenuItem>
            <MenuItem onClick={addColumnAfter} sx={{ color: colors.text, fontSize: '0.875rem' }}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add Column After
            </MenuItem>
            <MenuItem onClick={deleteColumn} sx={{ color: colors.text, fontSize: '0.875rem' }}>
              <RemoveIcon fontSize="small" sx={{ mr: 1 }} /> Delete Column
            </MenuItem>
            <Divider sx={{ borderColor: colors.border }} />
            <MenuItem onClick={addRowBefore} sx={{ color: colors.text, fontSize: '0.875rem' }}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add Row Before
            </MenuItem>
            <MenuItem onClick={addRowAfter} sx={{ color: colors.text, fontSize: '0.875rem' }}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add Row After
            </MenuItem>
            <MenuItem onClick={deleteRow} sx={{ color: colors.text, fontSize: '0.875rem' }}>
              <RemoveIcon fontSize="small" sx={{ mr: 1 }} /> Delete Row
            </MenuItem>
            <Divider sx={{ borderColor: colors.border }} />
            <MenuItem onClick={deleteTable} sx={{ color: palette.error, fontSize: '0.875rem' }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete Table
            </MenuItem>
          </>
        )}
      </Menu>

      <Box sx={{ flex: 1 }} />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
        tooltip="Undo"
        colors={colors}
      >
        <UndoIcon fontSize="small" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
        tooltip="Redo"
        colors={colors}
      >
        <RedoIcon fontSize="small" />
      </MenuButton>
    </Box>
  );
};

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Enter detailed description...',
  minHeight = 200,
  maxHeight = 400,
  disabled = false,
}: RichTextEditorProps) => {
  const { mode } = useContext(ColorModeContext);
  const colors = getColors(mode);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'specs-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update editor content when value prop changes (e.g., when data loads from API)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [editor, value]);

  return (
    <Box
      sx={{
        border: `1px solid ${colors.border}`,
        borderRadius: themeColors.radius.md,
        overflow: 'hidden',
        backgroundColor: colors.bg,
        transition: themeColors.transitions.fast,
        '&:focus-within': {
          borderColor: palette.primary,
          boxShadow: `0 0 0 2px ${palette.primary}20`,
        },
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <MenuBar editor={editor} colors={colors} disabled={disabled} />
      <Box
        sx={{
          minHeight,
          maxHeight,
          overflow: 'auto',
          p: 2,
          '& .tiptap': {
            outline: 'none',
            minHeight: minHeight - 32,
            color: colors.text,
            fontSize: typography.fontSize.sm,
            lineHeight: 1.6,
            '& p': {
              margin: '0 0 0.75em 0',
            },
            '& p:last-child': {
              marginBottom: 0,
            },
            '& h3': {
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.semibold,
              margin: '1em 0 0.5em 0',
            },
            '& ul, & ol': {
              margin: '0 0 0.75em 0',
              paddingLeft: '1.5em',
            },
            '& li': {
              margin: '0.25em 0',
            },
            '& blockquote': {
              borderLeft: `3px solid ${palette.primary}`,
              paddingLeft: '1em',
              margin: '0.75em 0',
              color: colors.textSecondary,
              fontStyle: 'italic',
            },
            '& pre': {
              backgroundColor: colors.bgElevated,
              borderRadius: themeColors.radius.sm,
              padding: '0.75em 1em',
              margin: '0.75em 0',
              overflow: 'auto',
              '& code': {
                backgroundColor: 'transparent',
                padding: 0,
                borderRadius: 0,
                fontSize: typography.fontSize.sm,
                fontFamily: 'monospace',
              },
            },
            '& code': {
              backgroundColor: colors.bgElevated,
              padding: '0.2em 0.4em',
              borderRadius: themeColors.radius.sm,
              fontSize: '0.9em',
              fontFamily: 'monospace',
            },
            '& a': {
              color: palette.primary,
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none',
              },
            },
            '& p.is-editor-empty:first-child::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: colors.textMuted,
              pointerEvents: 'none',
              height: 0,
            },
            // Table styles for specifications/characteristics
            '& table': {
              borderCollapse: 'collapse',
              margin: '0.75em 0',
              width: '100%',
              tableLayout: 'fixed',
              overflow: 'hidden',
              border: `1px solid ${colors.border}`,
              borderRadius: themeColors.radius.sm,
            },
            '& th, & td': {
              border: `1px solid ${colors.border}`,
              padding: '0.5em 0.75em',
              verticalAlign: 'top',
              boxSizing: 'border-box',
              position: 'relative',
              minWidth: '1em',
              '& > *': {
                marginBottom: 0,
              },
            },
            '& th': {
              backgroundColor: colors.bgElevated,
              fontWeight: typography.fontWeight.medium,
              textAlign: 'left',
            },
            '& td': {
              backgroundColor: colors.bg,
            },
            '& .selectedCell:after': {
              zIndex: 2,
              position: 'absolute',
              content: '""',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              backgroundColor: `${palette.primary}20`,
              pointerEvents: 'none',
            },
            '& .column-resize-handle': {
              position: 'absolute',
              right: -2,
              top: 0,
              bottom: -2,
              width: 4,
              backgroundColor: palette.primary,
              cursor: 'col-resize',
            },
            '&.resize-cursor': {
              cursor: 'col-resize',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
